import { Capacitor } from '@capacitor/core'
import { LocalStorageKeys } from '@shared/constants/LocalStorageKeys'
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin'
import { AUTH_TOKEN_STORAGE_CONFIG, AuthTokenStorageConfig } from './auth-storage/config'
import { IndexedDbAuthVault } from './auth-storage/indexed-db-vault'
import {
	assertAuthTokenSet,
	AuthStorageChange,
	AuthStorageMode,
	AuthStorageTransientError,
	AuthTokenRevision,
	AuthTokenSet,
	isStoredAuthTokens,
	sameRevision,
	StoredAuthTokens,
} from './auth-storage/types'

export type {
	AuthStorageChange,
	AuthStorageMode,
	AuthTokenRevision,
	AuthTokenSet,
	StoredAuthTokens,
} from './auth-storage/types'
export { AuthStorageCorruptError, AuthStorageTransientError } from './auth-storage/types'

type LockManagerLike = {
	request<T>(name: string, options: { mode: 'exclusive' }, callback: () => Promise<T>): Promise<T>
}

type BroadcastChannelLike = {
	postMessage(message: unknown): void
	addEventListener(type: 'message', listener: (event: MessageEvent<unknown>) => void): void
	removeEventListener(type: 'message', listener: (event: MessageEvent<unknown>) => void): void
	close(): void
}

type BroadcastChannelConstructor = new (name: string) => BroadcastChannelLike

type SecureStorageLike = {
	get(options: { key: string }): Promise<{ value?: string }>
	remove(options: { key: string }): Promise<unknown>
}

type LogoutMarkerStorageLike = {
	getItem(key: string): string | null
	setItem(key: string, value: string): void
	removeItem(key: string): void
}

export type AuthTokenStorageOptions = {
	config?: Partial<AuthTokenStorageConfig>
	indexedDB?: IDBFactory | null
	crypto?: Crypto | null
	lockManager?: LockManagerLike | null
	broadcastChannel?: BroadcastChannelConstructor | null
	secureStorage?: SecureStorageLike
	logoutMarkerStorage?: LogoutMarkerStorageLike | null
	isNativePlatform?: () => boolean
	now?: () => number
}

type NativeMeta = AuthTokenRevision & {
	schemaVersion: number
	tombstone: boolean
}

type BroadcastMessage = AuthStorageChange & {
	schemaVersion: number
}

const NATIVE_STORAGE_TIMEOUT_MS = 3_000
export const AUTH_LOGOUT_PENDING_KEY = 'tisit.auth.logout.pending.v1'
const NATIVE_TOKEN_KEY = 'tisit.auth.tokens.v2'
const NATIVE_META_KEY = 'tisit.auth.tokens.meta.v2'
const LEGACY_ACTIVE_GENERATION_KEY = `${LocalStorageKeys.accessToken}:activeGeneration`
const LEGACY_NATIVE_TOKEN_KEYS = [
	LocalStorageKeys.accessToken,
	LocalStorageKeys.refreshToken,
	`${LocalStorageKeys.accessToken}:storedAt`,
	`${LocalStorageKeys.accessToken}:generation`,
]
const LEGACY_WEB_TOKEN_KEYS = [
	LocalStorageKeys.token,
	LocalStorageKeys.accessToken,
	LocalStorageKeys.refreshToken,
	`${LocalStorageKeys.accessToken}:storedAt`,
	`${LocalStorageKeys.accessToken}:generation`,
	LEGACY_ACTIVE_GENERATION_KEY,
]

const withTimeout = async <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
	let timeoutId: ReturnType<typeof setTimeout> | undefined
	const timeout = new Promise<never>((_, reject) => {
		timeoutId = setTimeout(() => {
			reject(new AuthStorageTransientError('Secure storage operation timed out'))
		}, timeoutMs)
	})

	try {
		return await Promise.race([promise, timeout])
	} finally {
		if (timeoutId !== undefined) clearTimeout(timeoutId)
	}
}

const safeAtob = (value: string): string => {
	if (typeof globalThis.atob === 'function') return globalThis.atob(value)
	return ''
}

const base64UrlDecode = (value: string): string => {
	const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
	const padded = base64 + '==='.slice((base64.length + 3) % 4)
	return safeAtob(padded)
}

const decodeJwtPayload = (token: string): unknown => {
	const parts = token.split('.')
	if (parts.length < 2) return null
	try {
		return JSON.parse(base64UrlDecode(parts[1]))
	} catch {
		return null
	}
}

const jwtExpiresAt = (token: string | null): number | null => {
	if (!token) return null
	const payload = decodeJwtPayload(token)
	if (!payload || typeof payload !== 'object') return null
	const exp = (payload as { exp?: unknown }).exp
	if (typeof exp !== 'number' || !Number.isFinite(exp) || exp < 0) return null
	return exp * 1_000
}

const isNativeMeta = (value: unknown, schemaVersion: number): value is NativeMeta => {
	if (!value || typeof value !== 'object') return false
	const candidate = value as Partial<NativeMeta>
	return (
		candidate.schemaVersion === schemaVersion &&
		typeof candidate.tombstone === 'boolean' &&
		typeof candidate.generation === 'number' &&
		Number.isSafeInteger(candidate.generation) &&
		candidate.generation >= 0 &&
		typeof candidate.revision === 'number' &&
		Number.isSafeInteger(candidate.revision) &&
		candidate.revision >= 0
	)
}

const parseJson = (value: string | null): unknown => {
	if (value === null) return null
	try {
		return JSON.parse(value)
	} catch {
		return null
	}
}

const isMissingSecureStorageError = (error: unknown): boolean => {
	const message = typeof error === 'string' ? error : error instanceof Error ? error.message : ''
	return /(?:does not exist|not found|no value|missing)/i.test(message)
}

const isBroadcastMessage = (value: unknown, schemaVersion: number): value is BroadcastMessage => {
	if (!value || typeof value !== 'object') return false
	const candidate = value as Partial<BroadcastMessage>
	return (
		candidate.schemaVersion === schemaVersion &&
		(candidate.type === 'tokens-updated' || candidate.type === 'logout') &&
		typeof candidate.generation === 'number' &&
		Number.isSafeInteger(candidate.generation) &&
		candidate.generation >= 0 &&
		typeof candidate.revision === 'number' &&
		Number.isSafeInteger(candidate.revision) &&
		candidate.revision >= 0
	)
}

const isNewerRevision = (next: AuthTokenRevision, current: AuthTokenRevision): boolean =>
	next.generation > current.generation ||
	(next.generation === current.generation && next.revision > current.revision)

export class AuthTokenStorage {
	private readonly config: AuthTokenStorageConfig
	private readonly now: () => number
	private readonly secureStorage: SecureStorageLike
	private readonly logoutMarkerStorage: LogoutMarkerStorageLike | null
	private readonly nativePlatform: boolean
	private readonly lockManager: LockManagerLike | null
	private readonly webVault: IndexedDbAuthVault | null
	public readonly mode: AuthStorageMode
	private readonly listeners = new Set<(change: AuthStorageChange) => void>()
	private readonly broadcast: BroadcastChannelLike | null
	private currentRevision: AuthTokenRevision = { generation: 0, revision: 0 }
	private memoryTokens: StoredAuthTokens | null = null
	private localRefreshTail: Promise<void> = Promise.resolve()
	private nativeOperationTail: Promise<void> = Promise.resolve()
	private nativeMigrationChecked = false
	private nativeMigrationPromise: Promise<StoredAuthTokens | null> | null = null

	constructor(options: AuthTokenStorageOptions = {}) {
		this.config = { ...AUTH_TOKEN_STORAGE_CONFIG, ...options.config }
		this.now = options.now ?? (() => Date.now())
		this.secureStorage = options.secureStorage ?? SecureStoragePlugin
		this.nativePlatform = (options.isNativePlatform ?? (() => Capacitor.isNativePlatform()))()
		this.logoutMarkerStorage =
			options.logoutMarkerStorage === undefined
				? this.getDefaultLogoutMarkerStorage()
				: options.logoutMarkerStorage

		const indexedDB =
			options.indexedDB === undefined
				? typeof globalThis.indexedDB === 'undefined'
					? null
					: globalThis.indexedDB
				: options.indexedDB
		const crypto =
			options.crypto === undefined
				? typeof globalThis.crypto === 'undefined'
					? null
					: globalThis.crypto
				: options.crypto
		const browserLockManager =
			typeof navigator !== 'undefined' && 'locks' in navigator
				? (navigator.locks as LockManagerLike)
				: null
		this.lockManager =
			options.lockManager === undefined ? browserLockManager : options.lockManager

		const canUsePersistentWebVault = Boolean(
			indexedDB &&
				crypto?.subtle &&
				typeof crypto.getRandomValues === 'function' &&
				(this.nativePlatform || this.lockManager?.request)
		)

		if (canUsePersistentWebVault && indexedDB && crypto) {
			this.mode = 'webcrypto-indexeddb'
			this.webVault = new IndexedDbAuthVault({
				config: this.config,
				indexedDB,
				crypto,
				now: this.now,
				onObserve: revision => {
					if (
						isNewerRevision(revision, this.currentRevision) ||
						sameRevision(revision, this.currentRevision)
					) {
						this.currentRevision = {
							generation: revision.generation,
							revision: revision.revision,
						}
					}
				},
				onChange: (type, revision) => this.publishChange({ type, ...revision }),
			})
		} else {
			this.mode = 'memory-only'
			this.webVault = null
		}

		this.cleanupLegacyPlaintextWebTokens()
		this.broadcast = this.createBroadcastChannel(options.broadcastChannel)
	}

	private getDefaultLogoutMarkerStorage(): LogoutMarkerStorageLike | null {
		if (typeof window === 'undefined') return null
		try {
			return window.localStorage
		} catch {
			return null
		}
	}

	private hasPendingLogout(): boolean {
		if (!this.logoutMarkerStorage) return false
		try {
			return this.logoutMarkerStorage.getItem(AUTH_LOGOUT_PENDING_KEY) === '1'
		} catch (error) {
			throw new AuthStorageTransientError('Unable to read the pending logout marker', error)
		}
	}

	private markLogoutPending(): void {
		if (!this.logoutMarkerStorage) return
		try {
			this.logoutMarkerStorage.setItem(AUTH_LOGOUT_PENDING_KEY, '1')
		} catch (error) {
			throw new AuthStorageTransientError(
				'Unable to persist the pending logout marker',
				error
			)
		}
	}

	private clearPendingLogoutMarker(): void {
		if (!this.logoutMarkerStorage) return
		try {
			this.logoutMarkerStorage.removeItem(AUTH_LOGOUT_PENDING_KEY)
		} catch (error) {
			throw new AuthStorageTransientError('Unable to clear the pending logout marker', error)
		}
	}

	private getLegacyActiveGenerationState(): { generation: string; exists: boolean } {
		if (!this.logoutMarkerStorage) return { generation: '0', exists: false }
		try {
			const generation = this.logoutMarkerStorage.getItem(LEGACY_ACTIVE_GENERATION_KEY)
			return {
				generation: generation ?? '0',
				exists: generation !== null,
			}
		} catch (error) {
			throw new AuthStorageTransientError(
				'Unable to read the legacy active generation marker',
				error
			)
		}
	}

	private async completePendingLogout(pendingConfirmed = false): Promise<boolean> {
		if (!pendingConfirmed && !this.hasPendingLogout()) return false

		if (this.webVault) {
			await this.webVault.clear()
		} else {
			this.clearMemory()
		}
		await this.completeNativeStorageMigration()
		try {
			this.clearPendingLogoutMarker()
		} catch {
			// The authoritative tombstone is already committed. A leftover marker is
			// safe: the next boot will repeat the idempotent clear.
		}
		return true
	}

	private createBroadcastChannel(
		injected: BroadcastChannelConstructor | null | undefined
	): BroadcastChannelLike | null {
		if (this.nativePlatform || this.mode !== 'webcrypto-indexeddb') return null
		const constructor =
			injected === undefined
				? typeof window !== 'undefined' && typeof window.BroadcastChannel === 'function'
					? (window.BroadcastChannel as unknown as BroadcastChannelConstructor)
					: null
				: injected
		if (!constructor) return null

		try {
			const channel = new constructor(this.config.broadcastChannelName)
			channel.addEventListener('message', this.handleBroadcastMessage)
			return channel
		} catch {
			return null
		}
	}

	private readonly handleBroadcastMessage = (event: MessageEvent<unknown>): void => {
		if (!isBroadcastMessage(event.data, this.config.schemaVersion)) return
		this.applyChange(
			{
				type: event.data.type,
				generation: event.data.generation,
				revision: event.data.revision,
			},
			false
		)
	}

	private cleanupLegacyPlaintextWebTokens(): void {
		if (this.nativePlatform || typeof window === 'undefined') return
		let storages: Storage[]
		try {
			storages = [window.localStorage, window.sessionStorage]
		} catch {
			return
		}
		for (const storage of storages) {
			for (const key of LEGACY_WEB_TOKEN_KEYS) {
				try {
					storage.removeItem(key)
				} catch {
					// Storage privacy settings must not expose or re-import legacy tokens.
				}
			}
		}
	}

	private applyChange(change: AuthStorageChange, shouldBroadcast: boolean): void {
		const isCurrentOrNewer =
			isNewerRevision(change, this.currentRevision) ||
			sameRevision(change, this.currentRevision)
		if (!isCurrentOrNewer) return

		this.currentRevision = {
			generation: change.generation,
			revision: change.revision,
		}
		if (change.type === 'logout') {
			this.memoryTokens = null
		}
		for (const listener of this.listeners) listener(change)

		if (shouldBroadcast) {
			try {
				this.broadcast?.postMessage({
					schemaVersion: this.config.schemaVersion,
					...change,
				} satisfies BroadcastMessage)
			} catch {
				// Broadcast is advisory; IndexedDB metadata remains authoritative.
			}
		}
	}

	private publishChange(change: AuthStorageChange): void {
		this.applyChange(
			{
				type: change.type,
				generation: change.generation,
				revision: change.revision,
			},
			true
		)
	}

	private syncRevision(tokens: StoredAuthTokens | null): void {
		if (tokens && isNewerRevision(tokens, this.currentRevision)) {
			this.currentRevision = {
				generation: tokens.generation,
				revision: tokens.revision,
			}
		}
	}

	public getStorageMode(): AuthStorageMode {
		return this.mode
	}

	public getCurrentGeneration(): number {
		return this.currentRevision.generation
	}

	public getCurrentRevision(): AuthTokenRevision {
		return { ...this.currentRevision }
	}

	public subscribe(listener: (change: AuthStorageChange) => void): () => void {
		this.listeners.add(listener)
		return () => this.listeners.delete(listener)
	}

	private async nativeGetValue(key: string): Promise<string | null> {
		try {
			const result = await withTimeout(
				this.secureStorage.get({ key }),
				NATIVE_STORAGE_TIMEOUT_MS
			)
			return typeof result?.value === 'string' ? result.value : null
		} catch (error) {
			if (isMissingSecureStorageError(error)) return null
			throw error instanceof AuthStorageTransientError
				? error
				: new AuthStorageTransientError('Unable to read secure storage', error)
		}
	}

	private async nativeRemoveValue(key: string): Promise<void> {
		try {
			await withTimeout(this.secureStorage.remove({ key }), NATIVE_STORAGE_TIMEOUT_MS)
		} catch (error) {
			if (isMissingSecureStorageError(error)) return
			throw error instanceof AuthStorageTransientError
				? error
				: new AuthStorageTransientError('Unable to remove secure storage data', error)
		}
	}

	private async runNativeSerialized<T>(operation: () => Promise<T>): Promise<T> {
		const previous = this.nativeOperationTail
		let release: () => void = () => undefined
		this.nativeOperationTail = new Promise<void>(resolve => {
			release = resolve
		})
		await previous
		try {
			return await operation()
		} finally {
			release()
		}
	}

	private async clearNativeStorageArtifacts(): Promise<void> {
		if (!this.nativePlatform) return
		const keys = [NATIVE_TOKEN_KEY, NATIVE_META_KEY, ...LEGACY_NATIVE_TOKEN_KEYS]
		const results = await Promise.allSettled(keys.map(key => this.nativeRemoveValue(key)))
		const failure = results.find(
			(result): result is PromiseRejectedResult => result.status === 'rejected'
		)
		if (failure) throw failure.reason
	}

	private async completeNativeStorageMigration(): Promise<void> {
		if (!this.nativePlatform || !this.webVault || this.nativeMigrationChecked) return
		const state = await this.webVault.getLegacyMigrationState()
		if (!state.importClosed) {
			// Close the import path durably before touching the legacy plugin. Once
			// IndexedDB is authoritative, plugin availability must not gate auth.
			await this.webVault.markLegacyImportClosed()
		}
		this.nativeMigrationChecked = true
		if (state.cleanupComplete) return

		try {
			await this.clearNativeStorageArtifacts()
			await this.webVault.markLegacyCleanupComplete()
		} catch {
			// Best-effort compatibility cleanup. The import path is already closed,
			// so stale plugin data cannot resurrect a session.
			console.error('Unable to remove legacy native auth storage data')
		}
	}

	private async readNativeTokensForMigration(): Promise<StoredAuthTokens | null> {
		const [rawMeta, rawTokens] = await Promise.all([
			this.nativeGetValue(NATIVE_META_KEY),
			this.nativeGetValue(NATIVE_TOKEN_KEY),
		])
		const parsedMeta = parseJson(rawMeta)
		if (isNativeMeta(parsedMeta, this.config.schemaVersion)) {
			if (parsedMeta.tombstone) return null
			const parsedTokens = parseJson(rawTokens)
			return isStoredAuthTokens(parsedTokens) && sameRevision(parsedTokens, parsedMeta)
				? parsedTokens
				: null
		}

		const [accessToken, refreshToken, rawStoredAt, rawGeneration] = await Promise.all([
			this.nativeGetValue(LocalStorageKeys.accessToken),
			this.nativeGetValue(LocalStorageKeys.refreshToken),
			this.nativeGetValue(`${LocalStorageKeys.accessToken}:storedAt`),
			this.nativeGetValue(`${LocalStorageKeys.accessToken}:generation`),
		])
		if (!accessToken) return null

		const activeGeneration = this.getLegacyActiveGenerationState()
		if (
			(rawGeneration && rawGeneration !== activeGeneration.generation) ||
			(!rawGeneration && activeGeneration.exists)
		) {
			return null
		}

		const parsedStoredAt = Number(rawStoredAt)
		const parsedGeneration = Number(rawGeneration)
		const storedAt =
			Number.isFinite(parsedStoredAt) && parsedStoredAt >= 0 ? parsedStoredAt : this.now()
		return {
			accessToken,
			refreshToken: refreshToken || null,
			receivedAt: storedAt,
			storedAt,
			accessExpiresAt: jwtExpiresAt(accessToken),
			refreshExpiresAt: jwtExpiresAt(refreshToken),
			generation:
				Number.isSafeInteger(parsedGeneration) && parsedGeneration >= 0
					? parsedGeneration
					: 0,
			revision: 1,
		}
	}

	private async performNativeTokenMigration(): Promise<StoredAuthTokens | null> {
		if (!this.nativePlatform || !this.webVault || this.nativeMigrationChecked) return null
		if ((await this.webVault.getLegacyMigrationState()).importClosed) {
			await this.completeNativeStorageMigration()
			return null
		}
		const webRevision = this.getCurrentRevision()
		if (webRevision.generation !== 0 || webRevision.revision !== 0) {
			await this.completeNativeStorageMigration()
			return null
		}

		const nativeTokens = await this.runNativeSerialized(() =>
			this.readNativeTokensForMigration()
		)
		if (!nativeTokens) {
			await this.completeNativeStorageMigration()
			return null
		}

		const migrated = await this.webVault.saveNewSessionIfCurrent(webRevision, {
			accessToken: nativeTokens.accessToken,
			refreshToken: nativeTokens.refreshToken,
			receivedAt: nativeTokens.receivedAt,
			accessExpiresAt: nativeTokens.accessExpiresAt,
			refreshExpiresAt: nativeTokens.refreshExpiresAt,
		})
		if (!migrated) {
			const winner = await this.webVault.getTokens()
			if (winner) {
				await this.completeNativeStorageMigration()
			}
			return winner
		}

		const verified = await this.webVault.getTokens()
		if (!verified || !sameRevision(verified, migrated)) {
			throw new AuthStorageTransientError('Unable to verify migrated native auth tokens')
		}
		await this.completeNativeStorageMigration()
		return verified
	}

	private migrateNativeTokensIntoWebVault(): Promise<StoredAuthTokens | null> {
		if (this.nativeMigrationPromise) return this.nativeMigrationPromise
		const migration = this.performNativeTokenMigration()
		const trackedMigration = migration.finally(() => {
			if (this.nativeMigrationPromise === trackedMigration) {
				this.nativeMigrationPromise = null
			}
		})
		this.nativeMigrationPromise = trackedMigration
		return trackedMigration
	}

	public async getTokens(): Promise<StoredAuthTokens | null> {
		this.cleanupLegacyPlaintextWebTokens()
		if (this.mode === 'memory-only') {
			if (await this.completePendingLogout()) return null
			if (this.nativePlatform && !this.nativeMigrationChecked) {
				await this.clearNativeStorageArtifacts()
				this.nativeMigrationChecked = true
			}
			return this.memoryTokens ? { ...this.memoryTokens } : null
		}
		if (this.webVault) {
			const tokens = await this.webVault.getTokens()
			let pendingLogout = false
			try {
				pendingLogout = this.hasPendingLogout()
			} catch (error) {
				// If the durable vault already contains a tombstone, an unavailable
				// auxiliary marker cannot make the session active again.
				if (tokens) throw error
				this.syncRevision(null)
				return null
			}
			if (pendingLogout) {
				await this.completePendingLogout(true)
				return null
			}
			this.syncRevision(tokens)
			if (tokens) {
				await this.completeNativeStorageMigration()
				return tokens
			}
			const migrated = await this.migrateNativeTokensIntoWebVault()
			this.syncRevision(migrated)
			return migrated
		}

		return null
	}

	public async saveNewSession(tokens: AuthTokenSet): Promise<StoredAuthTokens> {
		assertAuthTokenSet(tokens)
		this.cleanupLegacyPlaintextWebTokens()
		await this.completePendingLogout()
		if (this.webVault) {
			const stored = await this.webVault.saveNewSession(tokens)
			await this.completeNativeStorageMigration()
			return stored
		}
		if (this.mode === 'memory-only') {
			const stored: StoredAuthTokens = {
				...tokens,
				storedAt: this.now(),
				generation: this.currentRevision.generation + 1,
				revision: 1,
			}
			this.memoryTokens = stored
			this.publishChange({ type: 'tokens-updated', ...stored })
			return { ...stored }
		}

		throw new AuthStorageTransientError('Auth token storage mode is unavailable')
	}

	public async saveNewSessionIfCurrent(
		expected: AuthTokenRevision,
		tokens: AuthTokenSet
	): Promise<StoredAuthTokens | null> {
		assertAuthTokenSet(tokens)
		this.cleanupLegacyPlaintextWebTokens()
		await this.completePendingLogout()
		if (this.webVault) {
			const stored = await this.webVault.saveNewSessionIfCurrent(expected, tokens)
			if (stored) await this.completeNativeStorageMigration()
			return stored
		}
		if (this.mode === 'memory-only') {
			if (!sameRevision(this.currentRevision, expected)) return null
			const stored: StoredAuthTokens = {
				...tokens,
				storedAt: this.now(),
				generation: expected.generation + 1,
				revision: 1,
			}
			this.memoryTokens = stored
			this.publishChange({ type: 'tokens-updated', ...stored })
			return { ...stored }
		}

		throw new AuthStorageTransientError('Auth token storage mode is unavailable')
	}

	public async compareAndSetRefresh(
		expected: AuthTokenRevision,
		tokens: AuthTokenSet
	): Promise<StoredAuthTokens | null> {
		assertAuthTokenSet(tokens)
		await this.completePendingLogout()
		if (this.webVault) {
			const stored = await this.webVault.compareAndSetRefresh(expected, tokens)
			if (stored) await this.completeNativeStorageMigration()
			return stored
		}
		if (this.mode === 'memory-only') {
			if (!this.memoryTokens || !sameRevision(this.memoryTokens, expected)) return null
			const stored: StoredAuthTokens = {
				...tokens,
				storedAt: this.now(),
				generation: expected.generation,
				revision: expected.revision + 1,
			}
			this.memoryTokens = stored
			this.publishChange({ type: 'tokens-updated', ...stored })
			return { ...stored }
		}

		throw new AuthStorageTransientError('Auth token storage mode is unavailable')
	}

	private clearMemory(
		expected?: AuthTokenRevision | { generation: number }
	): AuthTokenRevision | null {
		if (expected) {
			if (this.currentRevision.generation !== expected.generation) return null
			if ('revision' in expected && this.currentRevision.revision !== expected.revision)
				return null
		}
		const revision = {
			generation: this.currentRevision.generation + 1,
			revision: 0,
		}
		this.memoryTokens = null
		this.publishChange({ type: 'logout', ...revision })
		return revision
	}

	public async clearTokens(): Promise<void> {
		let markerPersisted = false
		try {
			this.markLogoutPending()
			markerPersisted = Boolean(this.logoutMarkerStorage)
		} catch {
			// The non-secret marker is a recovery aid. It must never prevent the
			// authoritative IndexedDB tombstone from being attempted.
		}
		if (this.webVault) {
			await this.webVault.clear()
			await this.completeNativeStorageMigration()
			if (markerPersisted) {
				try {
					this.clearPendingLogoutMarker()
				} catch {
					// Durable logout already won; keep the marker for a later cleanup.
				}
			}
			return
		}
		if (this.mode === 'memory-only') {
			this.clearMemory()
			if (this.nativePlatform) {
				await this.clearNativeStorageArtifacts()
				this.nativeMigrationChecked = true
			}
			if (markerPersisted) {
				try {
					this.clearPendingLogoutMarker()
				} catch {
					// Memory-only state is already gone for this realm.
				}
			}
			return
		}
	}

	public async clearTokensIfCurrent(expected: AuthTokenRevision): Promise<boolean> {
		if (this.webVault) {
			const cleared = Boolean(await this.webVault.clear(expected))
			if (cleared) await this.completeNativeStorageMigration()
			return cleared
		}
		if (this.mode === 'memory-only') return Boolean(this.clearMemory(expected))
		return false
	}

	public async clearTokensForGeneration(generation: number | string): Promise<void> {
		const parsedGeneration = Number(generation)
		if (!Number.isSafeInteger(parsedGeneration) || parsedGeneration < 0) return
		if (this.webVault) {
			const cleared = await this.webVault.clear({
				generation: parsedGeneration,
			})
			if (cleared) await this.completeNativeStorageMigration()
			return
		}
		if (this.mode === 'memory-only') {
			this.clearMemory({ generation: parsedGeneration })
			return
		}
	}

	public async runExclusiveRefresh<T>(operation: () => Promise<T>): Promise<T> {
		if (this.mode === 'webcrypto-indexeddb' && this.lockManager) {
			return this.lockManager.request(
				this.config.refreshLockName,
				{ mode: 'exclusive' },
				operation
			)
		}

		const previous = this.localRefreshTail
		let release: () => void = () => undefined
		this.localRefreshTail = new Promise<void>(resolve => {
			release = resolve
		})
		await previous
		try {
			return await operation()
		} finally {
			release()
		}
	}

	public withExclusiveRefresh<T>(operation: () => Promise<T>): Promise<T> {
		return this.runExclusiveRefresh(operation)
	}

	/**
	 * Compatibility shim for callers migrating to saveNewSession/compareAndSetRefresh.
	 */
	public async setTokens(
		accessToken: string,
		refreshToken: string | null,
		generation?: number | string
	): Promise<void> {
		const receivedAt = this.now()
		const tokens: AuthTokenSet = {
			accessToken,
			refreshToken,
			receivedAt,
			accessExpiresAt: jwtExpiresAt(accessToken),
			refreshExpiresAt: jwtExpiresAt(refreshToken),
		}
		if (generation === undefined) {
			await this.saveNewSession(tokens)
			return
		}

		const parsedGeneration = Number(generation)
		const current = await this.getTokens()
		if (
			!current ||
			!Number.isSafeInteger(parsedGeneration) ||
			current.generation !== parsedGeneration
		) {
			return
		}
		await this.compareAndSetRefresh(current, tokens)
	}

	public isTokenExpired(token: string, leewaySeconds: number = 30): boolean {
		const expiresAt = jwtExpiresAt(token)
		if (expiresAt === null) return true
		return this.now() + leewaySeconds * 1_000 >= expiresAt
	}

	public isAccessTokenExpired(accessToken: string, leewaySeconds: number = 30): boolean {
		return this.isTokenExpired(accessToken, leewaySeconds)
	}

	public isRefreshTokenExpired(refreshToken: string, leewaySeconds: number = 30): boolean {
		return this.isTokenExpired(refreshToken, leewaySeconds)
	}

	public dispose(): void {
		this.webVault?.close()
		if (this.broadcast) {
			this.broadcast.removeEventListener('message', this.handleBroadcastMessage)
			this.broadcast.close()
		}
		this.listeners.clear()
	}
}

export const authTokenStorage = new AuthTokenStorage()
